import type { Location } from '@functieloonhuis/shared';
import { DAY_LABELS_FULL, WORKWEEK_DAYS } from '../utils/weekUtils';

interface ScheduleFiltersProps {
  selectedDay: number | null;
  selectedLocation: Location | null;
  onDayChange: (day: number | null) => void;
  onLocationChange: (location: Location | null) => void;
  /**
   * `[id, label]` pairs for the team/department filter, already sorted.
   * The control only renders when there are >= 2 options — a manager of a
   * single department has nothing to choose between, while `resolveTeam`
   * returns EVERY department a user manages, so "eigen team" can span
   * several teams at once.
   */
  departments?: Array<[number, string]>;
  selectedDepartment?: number | null;
  onDepartmentChange?: (departmentId: number | null) => void;
}


export function ScheduleFilters({
  selectedDay,
  selectedLocation,
  onDayChange,
  onLocationChange,
  departments = [],
  selectedDepartment = null,
  onDepartmentChange,
}: ScheduleFiltersProps) {
  const showDepartmentFilter = departments.length >= 2 && onDepartmentChange !== undefined;
  // Niet afhankelijk van `showDepartmentFilter`: krimpt de afdelingslijst
  // terwijl er een afdeling gekozen is, dan moet de wis-actie juist blijven.
  const hasActiveFilters = selectedDay !== null || selectedLocation !== null || selectedDepartment !== null;

  return (
    <div className="flex items-center gap-3 flex-wrap">
      {/* Team/department filter */}
      {showDepartmentFilter && (
        <select
          aria-label="Filter op team"
          data-testid="werkrooster-afdeling-filter"
          value={selectedDepartment ?? ''}
          onChange={(e) => onDepartmentChange(e.target.value ? parseInt(e.target.value, 10) : null)}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="">Alle teams</option>
          {departments.map(([id, name]) => (
            <option key={id} value={id}>{name}</option>
          ))}
        </select>
      )}

      {/* Day filter */}
      <select
        value={selectedDay ?? ''}
        onChange={(e) => onDayChange(e.target.value ? parseInt(e.target.value) : null)}
        className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      >
        <option value="">Alle dagen</option>
        {WORKWEEK_DAYS.map(day => (
          <option key={day} value={day}>{DAY_LABELS_FULL[day]}</option>
        ))}
      </select>

      {/* Location filter */}
      <div className="inline-flex rounded-lg border border-gray-200 p-0.5 bg-gray-50">
        <button
          type="button"
          onClick={() => onLocationChange(null)}
          className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
            selectedLocation === null ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Alle
        </button>
        <button
          type="button"
          onClick={() => onLocationChange('kantoor')}
          className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
            selectedLocation === 'kantoor' ? 'bg-green-100 text-green-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Kantoor
        </button>
        <button
          type="button"
          onClick={() => onLocationChange('thuis')}
          className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
            selectedLocation === 'thuis' ? 'bg-blue-100 text-blue-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Thuis
        </button>
      </div>

      {/* Clear filters */}
      {hasActiveFilters && (
        <button
          type="button"
          onClick={() => { onDayChange(null); onLocationChange(null); onDepartmentChange?.(null); }}
          className="text-sm text-gray-500 hover:text-gray-700 underline"
        >
          Filters wissen
        </button>
      )}
    </div>
  );
}
